import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { AzTagComponent, AzTagStatus } from '../az-tag/az-tag.component';

export type ModalState =
  | 'confirm-change'
  | 'review'
  | 'confirm-reject'
  | 'success'
  | 'success-with-message'
  | 'rejected'
  | 'already-processed'
  | 'request-sent'
  | 'processing'
  | 'error'
  | 'custom-confirm';

@Component({
  selector: 'app-status-change-modal',
  standalone: true,
  imports: [AzTagComponent],
  templateUrl: './status-change-modal.component.html',
  styleUrl: './status-change-modal.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusChangeModalComponent {
  @Input() isOpen = false;
  @Input() state: ModalState = 'confirm-change';
  @Input() title = 'Status Change';
  @Input() customName = '';
  @Input() currentStatus = '';
  @Input() newStatus = '';
  @Input() alreadyProcessedBy = '';
  @Input() successMessage = '';
  @Input() errorMessage = '';
  @Input() warningMessage = '';
  @Input() warningLinkText = '';
  @Input() warningLinkUrl = '';

  // Custom confirm modal inputs
  @Input() customMessage = '';
  @Input() customNoButtonLabel = 'No';
  @Input() customYesButtonLabel = 'Yes';

  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() close = new EventEmitter<void>();
  @Output() confirmChange = new EventEmitter<void>();
  @Output() rejectRequested = new EventEmitter<void>();
  @Output() rejectCancelled = new EventEmitter<void>();
  @Output() customNo = new EventEmitter<void>();
  @Output() customYes = new EventEmitter<void>();

  onRequestChange() {
    this.confirmChange.emit();
  }

  onCancelChange() {
    this.close.emit();
  }

  onApprove() {
    this.approve.emit();
  }

  onReject() {
    this.rejectRequested.emit();
  }

  onConfirmReject() {
    this.reject.emit();
  }

  onCancelReject() {
    this.rejectCancelled.emit();
  }

  onClose() {
    this.close.emit();
  }

  onCustomNo() {
    this.customNo.emit();
  }

  onCustomYes() {
    this.customYes.emit();
  }

  onBackdropClick(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }

  getCurrentStatusPrimary(): AzTagStatus | '' {
    return this.getStatusPrimary(this.currentStatus);
  }

  getCurrentStatusSecondary(): string {
    return this.getStatusSecondary(this.currentStatus);
  }

  getNewStatusPrimary(): AzTagStatus | '' {
    return this.getStatusPrimary(this.newStatus);
  }

  getNewStatusSecondary(): string {
    return this.getStatusSecondary(this.newStatus);
  }

  private getStatusPrimary(status: string): AzTagStatus | '' {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return status.trim() as AzTagStatus;
    return status.slice(0, idx).trim() as AzTagStatus;
  }

  private getStatusSecondary(status: string): string {
    if (!status) return '';
    const idx = status.indexOf(':');
    if (idx === -1) return '';
    return status.slice(idx + 1).trim();
  }
}
